import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import butterflyApi from '../ButterflyApi';
import MobilePage from '../UiComponents/MobilePage';
import { isLogin } from '../Utils/Cookies';
import { callName } from '../Utils/Strings';
import { getAppProductionStatusText, replaceAppTitleCallName } from './appProductionStatus';
import './Orders.css';
import './OrderDetail.css';

const PRODUCTION_MESSAGES = {
  REQUESTED: {
    title: '제작 접수를 기다리고 있어요',
    description: '신청하신 주문의 접수를 기다리고 있어요. 진행 상태는 이 화면에서 확인할 수 있어요.',
  },
  ACCEPTED: {
    title: '제작 요청이 접수되었어요',
    description: '우리 아이를 위한 선물을 준비하고 있어요. 제작 상태는 순차적으로 업데이트돼요.',
  },
  IN_PROGRESS: {
    title: '우리 아이의 선물을 만들고 있어요',
    description: '제작이 진행 중이에요. 완성된 결과는 이 화면에서 확인할 수 있어요.',
  },
  COMPLETED: {
    title: '제작이 완료되었어요',
    description: '결과가 연결되면 이 화면에서 확인할 수 있어요.',
  },
  CANCELED: {
    title: '제작이 취소되었어요',
    description: '취소된 주문이에요. 주문과 관련해 궁금한 점은 1:1 문의로 남겨주세요.',
  },
}

const formatPrice = (price) => {
  if (price === undefined || price === null) return ''
  return `${new Intl.NumberFormat('ko-KR').format(price)}원`
}

const formatDate = (datetime) => {
  if (!datetime) return '-'
  return datetime.replace('T', ' ')
}

const getLocalHeroes = () => {
  try {
    return JSON.parse(localStorage.getItem('heroes')) || []
  } catch (e) {
    return []
  }
}

const replaceCallName = (text, heroCallName) => {
  return (text || '').replace(/\{callName\}/g, heroCallName)
}

const getHeroCallName = (production, heroes) => {
  const hero = heroes.find(hero => `${hero.heroNo}` === `${production.heroNo}`)
  if (hero) return callName(hero.firstName)
  if (production.heroFirstName) return callName(production.heroFirstName)
  if (production.firstName) return callName(production.firstName)
  if (production.heroName) return callName(production.heroName)
  return '하늘이'
}

const getProductionAppName = (production, heroCallName) => {
  return replaceAppTitleCallName(production.app?.title || production.app?.appName || production.title || production.appName, production, heroCallName)
}

const getProductionAppDescription = (production, heroCallName) => {
  return replaceCallName(production.app?.description || production.appDescription || production.description, heroCallName)
}

const AppProductionDetail = () => {
  const location = useLocation();
  const { productionNo } = useParams();
  const [production, setProduction] = useState()
  const [loading, setLoading] = useState(true)
  const heroes = getLocalHeroes()
  const listPath = location.pathname.startsWith('/orders') ? '/orders' : '/app-productions'

  const load = useCallback(async () => {
    if (!isLogin()) {
      setLoading(false)
      return
    }

    setLoading(true)
    const response = await butterflyApi.getAppProduction(productionNo)
    setProduction(response)
    setLoading(false)
  }, [productionNo])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
    load()
  }, [productionNo, load])

  if (loading) {
    return <MobilePage className='storybook-page orders-page order-detail-page' loading loadingMessage="주문 정보를 확인하고 있습니다." />
  }

  const loggedIn = Boolean(isLogin())

  if (!loggedIn || !production) {
    return <MobilePage className='storybook-page orders-page order-detail-page'>
      <main className='orders-content'>
        <Link className='book-text-link orders-home' to={listPath}><span aria-hidden='true'>‹</span> 주문내역</Link>
        <header className='orders-intro order-detail-intro'>
          <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 우리 아이를 위한 특별한 선물</p>
          <h1 className='book-title'>주문 상세</h1>
          <p className='book-description'>신청한 선물의 제작 상태와<br />주문 정보를 확인하는 곳이에요.</p>
        </header>
        <section className='orders-empty' aria-labelledby='order-detail-empty-title'>
          <span className='orders-empty-icon' aria-hidden='true'>✧</span>
          <h2 id='order-detail-empty-title'>{loggedIn ? '주문 정보를 찾을 수 없어요' : '로그인이 필요해요'}</h2>
          <p>{loggedIn ? '주문내역에서 확인할 주문을 다시 선택해 주세요.' : '로그인하면 주문 정보와 제작 진행 상황을 확인할 수 있어요.'}</p>
          <Link
            className='book-primary-button'
            to={loggedIn ? listPath : `/login?redirect=${listPath}/${productionNo}`}
          >
            {loggedIn ? '주문내역으로 돌아가기' : '로그인하기'} <span aria-hidden='true'>→</span>
          </Link>
        </section>
      </main>
    </MobilePage>
  }

  const completed = production.productionStatus === 'COMPLETED' && production.resultUrl
  const heroCallName = getHeroCallName(production, heroes)
  const appName = getProductionAppName(production, heroCallName)
  const appDescription = getProductionAppDescription(production, heroCallName)
  const statusText = getAppProductionStatusText(production.productionStatus)
  const statusMessage = completed ? {
    title: '우리 아이의 선물이 완성됐어요',
    description: '기다려 주셔서 감사해요. 아래 버튼을 눌러 완성된 선물을 만나보세요.',
  } : PRODUCTION_MESSAGES[production.productionStatus] || {
    title: '주문의 제작 상태를 확인해 주세요',
    description: '주문과 관련해 궁금한 점은 1:1 문의로 남겨주세요.',
  }

  return <MobilePage className='storybook-page orders-page order-detail-page'>
    <main className='orders-content'>
      <Link className='book-text-link orders-home' to={listPath}><span aria-hidden='true'>‹</span> 주문내역</Link>

      <header className='orders-intro order-detail-intro'>
        <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 주문 상세</p>
        <h1 className='book-title'>{appName || '주문 상세'}</h1>
        {appDescription && <p className='book-description'>{appDescription}</p>}
        {production.heroName && <div className='order-detail-hero'>
          <span>이 선물의 주인공</span><strong>{production.heroName}</strong>
        </div>}
      </header>

      <section className='order-detail-panel order-detail-status' data-status={production.productionStatus} aria-labelledby='order-detail-status-title'>
        <div className='order-detail-section-heading'>
          <h2 id='order-detail-status-title'>제작 상태</h2>
          {statusText && <span className='orders-status' data-status={production.productionStatus}>
            <span className='orders-status-dot' aria-hidden='true' />{statusText}
          </span>}
        </div>
        <div className='order-detail-status-note'>
          <span className='order-detail-status-icon' aria-hidden='true'>{production.productionStatus === 'COMPLETED' ? '✓' : production.productionStatus === 'CANCELED' ? '−' : '✧'}</span>
          <div>
            <h3>{statusMessage.title}</h3>
            <p>{statusMessage.description}</p>
          </div>
        </div>
        {completed && <button
          type='button'
          className='book-primary-button order-detail-result'
          onClick={() => { window.location.href = production.resultUrl }}
        >
          결과 확인하기 <span aria-hidden='true'>↗</span>
        </button>}
      </section>

      <section className='order-detail-panel order-detail-payment' aria-labelledby='order-detail-payment-title'>
        <h2 id='order-detail-payment-title'>결제 및 주문 정보</h2>
        <dl className='order-detail-receipt'>
          <div className='order-detail-amount'>
            <dt>결제 금액</dt>
            <dd>{formatPrice(production.amount) || '—'}</dd>
          </div>
          <div>
            <dt>주문 일시</dt>
            <dd>{production.registerDatetime ? <time dateTime={production.registerDatetime}>{formatDate(production.registerDatetime)}</time> : '—'}</dd>
          </div>
          <div>
            <dt>완료 일시</dt>
            <dd>{production.completedDatetime ? <time dateTime={production.completedDatetime}>{formatDate(production.completedDatetime)}</time> : '—'}</dd>
          </div>
        </dl>
      </section>

      <Link className='order-detail-help' to='/support/inquiries'>
        <span className='order-detail-help-icon' aria-hidden='true'>?</span>
        <span><strong>주문에 대해 궁금한 점이 있나요?</strong><small>1:1 문의로 편하게 남겨주세요.</small></span>
        <span className='order-detail-help-arrow' aria-hidden='true'>↗</span>
      </Link>
      <Link className='book-secondary-button order-detail-return' to={listPath}>주문내역으로 돌아가기</Link>
      <p className='orders-footnote'><span aria-hidden='true'>✦</span> 아이를 위한 마음을, 특별한 선물로 담아요.</p>
    </main>
  </MobilePage>
}

export default AppProductionDetail
